import React from 'react';
import styled from '@emotion/styled';
import { ApiPrice } from '@ibe/api';
import { ConfigModel } from '@ibe/services';
import useAppService from '../../../Hooks/useAppService';
import { customRoundNumber } from '../../../../Utils/globals';
import { useExternalFormatPrice } from '../../../Hooks/useExternalFormatPrice';

// TODO showModifiers (in tooltip-portal)
// TODO showTaxes (in tooltip-portal)

export interface Props {
  price: ApiPrice | null | undefined; // TODO model is not ok!!! see /Models/Price!!! (see git log OAG!)
  prefix?: string;
  suffix?: string;
  displayInline?: boolean;
  showTaxes?: boolean;
  showModifiers?: boolean;
  showReduction?: boolean;
  align?: 'left' | 'right' | 'center' | 'justify';
  className?: string;
  removeZeroDecimals?: boolean;
  roundPrice?: ConfigModel['roundPricesToWholeNumber'];
  useGrouping?: boolean;
}

export const Price = (p: Props): JSX.Element => {
  const appService = useAppService();
  const formatPrice = useExternalFormatPrice();

  if (!p.price || !p.price?.currencyCode) {
    return <></>;
  }

  const { price, removeZeroDecimals, roundPrice, useGrouping = true } = p;
  const prefix = p.prefix || '';
  const suffix = p.suffix || '';
  const align = p.align || 'right';
  const className = p.className || '';
  const displayInline = p.displayInline || false;
  const showReduction =
    p.showReduction && price.basePrice ? price.finalPrice < price.basePrice : false;
  const showTaxes = p.showTaxes || false;
  const showModifiers = p.showModifiers || false;

  const f = (amount: number): string =>
    formatPrice(
      amount,
      price.currencyCode,
      appService.lang,
      removeZeroDecimals || !!roundPrice,
      useGrouping
    );
  const ff = (amount: number): JSX.Element => {
    return (
      <>
        {prefix && <span className="iso__price__prefix">{prefix}&nbsp;</span>}
        <span className="iso__price__value">{f(amount).replace('\u00A0', ' ')}</span>
        {suffix && <span className="iso__price__suffix">&nbsp;{suffix}</span>}
      </>
    );
  };

  const Old = styled.span`
    position: relative;
    &::after {
      content: '';
      width: 100%;
      height: 2px;
      background: #000000;
      position: absolute;
      top: 50%;
      left: 0;
      opacity: 0.75;
    }
  `;

  const New = styled.span`
    color: #000000;
  `;

  const Sales = styled.span`
    color: #c61515;
  `;

  const Line = styled.span`
    text-align: ${align};
    display: ${displayInline ? 'inline-block' : 'block'};
  `;

  const Inline = styled.span`
    text-align: ${align};
  `;

  if (!price) {
    return <></>;
  }

  const discount =
    price && price.modifiers
      ? price.modifiers.find(modifier => modifier.type === 'DISCOUNT')
      : undefined;

  const Modifiers = (): JSX.Element => {
    return (
      <>
        {price.modifiers
          .filter(m => m.type === 'TAX')
          .map(modifier => (
            <>
              {modifier.description} {f(modifier.absolute)} {modifier.name}
              <br />
            </>
          ))}
      </>
    );
  };

  return (
    <Inline className={className}>
      {showReduction ? (
        <Line>
          <Old>{f(price.basePrice || 0)}</Old> <New>{f(price.finalPrice)}</New>
          {price.modifiers.length > 0 && (
            <>
              <br />
              {discount && (
                <Sales>
                  {discount.description} / {discount.name} {f(discount.absolute)}
                </Sales>
              )}
            </>
          )}
        </Line>
      ) : (
        price &&
        (price.finalPrice || price.finalPrice === 0) && (
          <Line>{ff(customRoundNumber(price.finalPrice, roundPrice))}</Line>
        )
      )}

      {showReduction && showModifiers && (
        <Line className="small text-muted">
          <Modifiers />
        </Line>
      )}

      {showTaxes && (
        <Line className="small text-muted">
          <Modifiers />
        </Line>
      )}
    </Inline>
  );
};
